/* eslint-disable css-modules/no-unused-class, no-extra-boolean-cast, prefer-template */
import { FC } from 'react';
import classNames from 'classnames';
import { translate } from 'translations';
import { Avatar, Icon } from 'learning-panel-components';
import LessonProgressBorder from './progressBorders/lessonProgressBorder';
import CourseProgressBorder from './progressBorders/courseProgressBorder';
import styles from './contentItem.module.scss';

const ContentItem: FC<ContentItemProps> = (props: ContentItemProps) => {
    const {
        id,
        progress,
        outerLineTo,
        type,
        userNickname,
        userAvatar,
        status,
        duration,
        name,
        points,
        isHovered,
        onSelectNode,
        numberOfItems,
        setInventoryId,
        showUserAvatar,
        isOptional,
        isRoot
    } = props;

    const typesImages = {
        course: {
            topIcon: `${process.env.PUBLIC_URL}/images/courseTopIcon.svg`,
            optionalIcon: `${process.env.PUBLIC_URL}/images/courseOptional.svg`,
            mainImage: `${process.env.PUBLIC_URL}/images/course.webp`,
            outerRight: `${process.env.PUBLIC_URL}/images/lessonOuterRight.svg`,
            outerLeft: `${process.env.PUBLIC_URL}/images/lessonOuterLeft.svg`,
            progressElement: <LessonProgressBorder percentage={progress} />,
            wrapperClass: styles['content-item-wrapper']
        },
        lesson: {
            topIcon: `${process.env.PUBLIC_URL}/images/lessonTopIcon.svg`,
            optionalIcon: `${process.env.PUBLIC_URL}/images/lessonOptional.svg`,
            mainImage: `${process.env.PUBLIC_URL}/images/lesson.webp`,
            outerRight: `${process.env.PUBLIC_URL}/images/lessonOuterRight.svg`,
            outerLeft: `${process.env.PUBLIC_URL}/images/lessonOuterLeft.svg`,
            progressElement: <LessonProgressBorder percentage={progress} />,
            wrapperClass: styles['content-item-wrapper']
        },
        challenge: {
            topIcon: `${process.env.PUBLIC_URL}/images/challengeTopIcon.svg`,
            optionalIcon: `${process.env.PUBLIC_URL}/images/challengeOptional.svg`,
            mainImage: `${process.env.PUBLIC_URL}/images/challenge.svg`,
            outerRight: `${process.env.PUBLIC_URL}/images/lessonOuterRight.svg`,
            outerLeft: `${process.env.PUBLIC_URL}/images/lessonOuterLeft.svg`,
            progressElement: <LessonProgressBorder percentage={progress} />,
            wrapperClass: styles['content-item-wrapper']
        },
        assessment: {
            topIcon: `${process.env.PUBLIC_URL}/images/assessmentTopIcon.svg`,
            optionalIcon: `${process.env.PUBLIC_URL}/images/assessmentOptional.svg`,
            mainImage: `${process.env.PUBLIC_URL}/images/assessment.svg`,
            outerRight: `${process.env.PUBLIC_URL}/images/lessonOuterRight.svg`,
            outerLeft: `${process.env.PUBLIC_URL}/images/lessonOuterLeft.svg`,
            progressElement: <LessonProgressBorder percentage={progress} />,
            wrapperClass: styles['content-item-wrapper']
        },
        mixed: {
            topIcon: `${process.env.PUBLIC_URL}/images/mixTop.svg`,
            optionalIcon: `${process.env.PUBLIC_URL}/images/nodeOptional.svg`,
            mainImage: `${process.env.PUBLIC_URL}/images/mixed.svg`,
            outerRight: `${process.env.PUBLIC_URL}/images/courseOuterRight.svg`,
            outerLeft: `${process.env.PUBLIC_URL}/images/courseOuterLeft.svg`,
            progressElement: <CourseProgressBorder percentage={progress} />,
            wrapperClass: styles['content-item-wrapper-mixed']
        }
    };

    return (
        <div className={styles['margin-wrapper']}>
            <div
                className={classNames(typesImages[type].wrapperClass, {
                    [styles.completed]: status === 'completed',
                    [styles.takeable]: status === 'takable',
                    [styles['not-started']]: status === 'not_started' || (isRoot && status === 'notTakable'),
                    [styles['in-progress']]: status === 'started',
                    [styles['not-takable']]: status === 'notTakable' && !isRoot,
                    [styles.hovered]: isHovered
                })}
                style={{ '--img': 'url(' + typesImages[type].mainImage + ')' } as any}
                onClick={status !== 'notTakable' || isRoot ? (): void => onSelectNode() : undefined}
            >
                {isOptional && <div className={styles['optional-musk']} />}
                {((status === 'notTakable' && !isRoot) || status === 'completed') && (
                    <div className={styles['opacity-layer']} />
                )}
                <img
                    alt="contentItemsIcons"
                    src={isOptional ? typesImages[type].optionalIcon : typesImages[type].topIcon}
                    className={styles['top-icon']}
                />
                <div className={styles['top-details']}>
                    {status !== 'completed' && (
                        <div className={styles['detail-wrapper']}>
                            <Icon name="clock" />
                            <div>{translate('x_Mins', { x: String(duration) })}</div>
                        </div>
                    )}
                    {status !== 'completed' && <hr />}
                    <div className={styles['detail-wrapper']}>
                        {status === 'notTakable' && <div className={styles.max}>{translate('Max')}</div>}
                        <Icon name="devCoin" />
                        <div>{points}</div>
                    </div>
                    <div></div>
                </div>
                {type === 'mixed' && (
                    <div
                        className={styles['info-wrapper']}
                        onClick={e => {
                            e.stopPropagation();
                            setInventoryId(id);
                        }}
                    >
                        <div className={styles.number}>{numberOfItems}</div>
                        <Icon name="info" className={styles.icon} />
                    </div>
                )}
                <div className={styles['name-background']}></div>
                <div className={styles.name} title={translate(name)}>
                    {translate(name)}
                </div>
                {progress !== 0 && (
                    <div className={styles['progress-wrapper']}>{typesImages[type].progressElement}</div>
                )}
                {outerLineTo && (
                    <img
                        alt="borderLine"
                        src={outerLineTo === 'left' ? typesImages[type].outerLeft : typesImages[type].outerRight}
                        className={styles[outerLineTo === 'left' ? 'outer-left' : 'outer-right']}
                    />
                )}
                {showUserAvatar && (
                    <div className={styles['avatar-wrapper']}>
                        <Avatar name={userNickname} avatarSrc={userAvatar} />
                    </div>
                )}
            </div>
        </div>
    );
};

export interface ContentItemProps {
    id: string;
    progress: number;
    outerLineTo?: 'left' | 'right';
    status: ContentItemStatus;
    type: 'course' | 'lesson' | 'challenge' | 'assessment' | 'mixed';
    userAvatar?: string;
    userNickname: string;
    duration: number;
    name: string;
    points: number;
    isHovered: boolean;
    onSelectNode: Function;
    numberOfItems: number;
    setInventoryId: Function;
    showUserAvatar: boolean;
    isOptional?: boolean;
    isRoot?: boolean;
}

export type ContentItemStatus = 'started' | 'not_started' | 'completed' | 'takable' | 'notTakable';

export default ContentItem;
